import React, { FC, useEffect, useState } from 'react';

import { trackEvent } from '@app/services/analytics.service';
import { notificationService } from '@app/services/notification.service';
import { doLoginByPhone, doLoginPre } from '@app/store/slices/authSlice';
import { useLoginRedirect } from '@FW/hooks/useLoginRedirect';
import { useAppDispatch } from '@hooks/shared/reduxHooks';
import { StdApiError } from '@interfaces/FW/StdApiError';
import { isValidInternationalPhone, toWireDigits } from '@shared/utils/number/internationalPhone';
import { Button, Flex, Form, Input } from 'antd';
import PhoneInput, { PhoneNumber } from 'antd-phone-input';
import { useTranslation } from 'react-i18next';

/** LoginPre rejects a resend issued less than this long after the previous one. */
const RESEND_COOLDOWN_SECONDS = 60;
const OTP_PATTERN = /^\d+$/;

type LoginPhase = 'phone' | 'otp';

interface ILoginByPhoneFormData {
	phone: PhoneNumber | null;
	otp: string;
}

interface IResendLinkProps {
	cooldownEndsAt: number | null;
	disabled: boolean;
	onClick: () => void;
}

const ResendLink: FC<IResendLinkProps> = ({ cooldownEndsAt, disabled, onClick }) => {
	const { t } = useTranslation();
	const computeRemaining = () =>
		cooldownEndsAt ? Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000)) : 0;
	const [remaining, setRemaining] = useState<number>(computeRemaining);

	useEffect(() => {
		setRemaining(computeRemaining());
		if (!cooldownEndsAt || cooldownEndsAt <= Date.now()) return;

		const id = setInterval(() => {
			const next = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
			setRemaining(next);
			if (next === 0) clearInterval(id);
		}, 1000);
		return () => clearInterval(id);
	}, [cooldownEndsAt]);

	return (
		<Button type='link' className='p-0--i' disabled={disabled || remaining > 0} onClick={onClick}>
			{remaining > 0 ? t('auth.otpResendIn', { seconds: remaining }) : t('auth.otpResend')}
		</Button>
	);
};

/**
 * Primary sign-in path: phone number in, one-time code back. Rendered as a
 * block inside the login screen — the surrounding card owns the page chrome.
 */
const LoginByPhoneForm: React.FC = () => {
	const [form] = Form.useForm<ILoginByPhoneFormData>();
	const [phase, setPhase] = useState<LoginPhase>('phone');
	const [loading, setLoading] = useState(false);
	const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);

	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const redirectAfterLogin = useLoginRedirect();

	const phoneValue = Form.useWatch('phone', form);
	const phoneDigits = toWireDigits(phoneValue);
	const isPhoneValid = phoneDigits !== null && isValidInternationalPhone(phoneDigits);

	/**
	 * `getErrorData()` labels every 401 "session expired" and every unlabelled
	 * failure with a generic message. Neither fits this screen: here the backend
	 * answers 401 for "no such user / user disabled" and 403 for a rejected phone
	 * or code.
	 */
	const resolveErrorMessage = (err: StdApiError, rejectedMessage: string): string => {
		if (err.status === 401) return t('auth.phoneNotFound');
		if (err.status === 403) return rejectedMessage;

		return err.message ? err.message : t('api.error');
	};

	const sendOtp = () => {
		if (!phoneDigits) return;

		// The resend link reuses this handler. Only the first send of an attempt is
		// the funnel's denominator — counting resends would inflate it.
		const isFirstSend = phase === 'phone';

		setLoading(true);

		dispatch(doLoginPre({ phone: phoneDigits }))
			.unwrap()
			.then(() => {
				if (isFirstSend) {
					trackEvent('login', { login_status: 'otp_sent', login_method: 'otp' });
				}
				notificationService.success({ message: t('auth.otpSentToast') });
				setPhase('otp');
				setCooldownEndsAt(Date.now() + RESEND_COOLDOWN_SECONDS * 1000);
			})
			.catch((err: StdApiError) => {
				notificationService.error({
					message: resolveErrorMessage(err, t('auth.otpSendFailed')),
				});
			})
			.finally(() => setLoading(false));
	};

	const submitForm = (values: ILoginByPhoneFormData) => {
		if (phase === 'phone') {
			sendOtp();
			return;
		}

		if (!phoneDigits) return;

		setLoading(true);

		dispatch(doLoginByPhone({ phone: phoneDigits, otp: values.otp }))
			.unwrap()
			.then(
				() => redirectAfterLogin({ loginMethod: 'otp' }),
				(err: StdApiError) => {
					// Scoped to the login call itself: a failure while loading clients
					// afterwards is not a login failure. 403 is the backend rejecting the
					// code (see resolveErrorMessage); anything else is the request not landing.
					trackEvent('login', {
						login_status: err.status === 403 ? 'otp_wrong' : 'otp_failed',
						login_method: 'otp',
					});
					throw err;
				},
			)
			.catch((err: StdApiError) => {
				notificationService.error({
					message: resolveErrorMessage(err, t('auth.otpInvalid')),
				});
			})
			.finally(() => setLoading(false));
	};

	/** Editing the number after a code was sent invalidates it — go back to step 1. */
	const handleValuesChange = (changed: Partial<ILoginByPhoneFormData>) => {
		if ('phone' in changed && phase === 'otp') {
			setPhase('phone');
			form.setFieldValue('otp', '');
		}
	};

	return (
		<Form
			form={form}
			layout='vertical'
			size='large'
			requiredMark={false}
			onFinish={submitForm}
			onValuesChange={handleValuesChange}>
			<Flex vertical gap={16}>
				<Form.Item<ILoginByPhoneFormData>
					name='phone'
					label={t('auth.phoneLabel')}
					style={{ marginBottom: 0 }}
					rules={[
						{
							validator: (_, value) => {
								const digits = toWireDigits(value);

								if (!digits) return Promise.reject(new Error(t('auth.phoneRequired')));
								if (!isValidInternationalPhone(digits)) {
									return Promise.reject(new Error(t('auth.phoneInvalid')));
								}

								return Promise.resolve();
							},
						},
					]}>
					<PhoneInput
						distinct
						allowClear
						size='large'
						autoFocus
						country='ua'
						excludeCountries={['ru']}
						enableSearch
						searchPlaceholder={t('businessPhones.searchPlaceholder')}
						searchNotFound={t('businessPhones.searchNotFound')}
					/>
				</Form.Item>

				{phase === 'otp' && (
					<Form.Item<ILoginByPhoneFormData>
						name='otp'
						label={t('auth.otpCodeLabel')}
						style={{ marginBottom: 0 }}
						rules={[
							{ required: true, message: t('auth.otpRequired') },
							{ pattern: OTP_PATTERN, message: t('auth.otpDigitsOnly') },
						]}>
						<Input autoFocus inputMode='numeric' autoComplete='one-time-code' />
					</Form.Item>
				)}

				<Button type='primary' htmlType='submit' block loading={loading} disabled={!isPhoneValid}>
					{phase === 'otp' ? t('auth.buttonText') : t('auth.getCode')}
				</Button>

				{phase === 'otp' && (
					<Flex justify='flex-start'>
						<ResendLink
							cooldownEndsAt={cooldownEndsAt}
							disabled={loading || !isPhoneValid}
							onClick={sendOtp}
						/>
					</Flex>
				)}
			</Flex>
		</Form>
	);
};

export default LoginByPhoneForm;
